import { createEnum } from "@redotech/zod/zod-enum";
import * as z from "zod/v4";

// Message Part
export interface TextPart extends z.infer<typeof TextPartSchema_> {}
export const TextPartSchema_ = z.object({
  type: z.literal("text"),
  text: z.string(),
});
export const TextPartSchema: z.ZodType<TextPart> = TextPartSchema_;

export interface ImagePart extends z.infer<typeof ImagePartSchema_> {}
export const ImagePartSchema_ = z.object({
  type: z.literal("image"),
  image: z.url(),
  providerOptions: z
    .record(z.string(), z.record(z.string(), z.json()))
    .optional(),
});
export const ImagePartSchema: z.ZodType<ImagePart> = ImagePartSchema_;

export interface FilePart extends z.infer<typeof FilePartSchema_> {}
export const FilePartSchema_ = z.object({
  type: z.literal("file"),
  data: z.url(),
  mimeType: z.string(),
});
export const FilePartSchema: z.ZodType<FilePart> = FilePartSchema_;

export interface ToolCallPart extends z.infer<typeof ToolCallPartSchema_> {}
export const ToolCallPartSchema_ = z.object({
  type: z.literal("tool-call"),
  toolCallId: z.string(),
  toolName: z.string(),
  args: z.json(),
});
export const ToolCallPartSchema: z.ZodType<ToolCallPart> = ToolCallPartSchema_;

export interface ToolResultPart extends z.infer<typeof ToolResultPartSchema_> {}
export const ToolResultPartSchema_ = z.object({
  type: z.literal("tool-result"),
  toolCallId: z.string(),
  toolName: z.string(),
  result: z.json(),
  isError: z.boolean().optional(),
  errorMetadata: z.record(z.string(), z.json()).optional(),
});
export const ToolResultPartSchema: z.ZodType<ToolResultPart> =
  ToolResultPartSchema_;

export interface ReasoningPart extends z.infer<typeof ReasoningPartSchema_> {}
export const ReasoningPartSchema_ = z.object({
  type: z.literal("reasoning"),
  text: z.string(),
  signature: z.string().optional(),
});
export const ReasoningPartSchema: z.ZodType<ReasoningPart> =
  ReasoningPartSchema_;

export interface RedactedReasoningPart
  extends z.infer<typeof RedactedReasoningPartSchema_> {}
export const RedactedReasoningPartSchema_ = z.object({
  type: z.literal("redacted-reasoning"),
  data: z.string(),
});
export const RedactedReasoningPartSchema: z.ZodType<RedactedReasoningPart> =
  RedactedReasoningPartSchema_;

export const UserMessageContentSchema = z.union([
  TextPartSchema,
  ImagePartSchema,
  FilePartSchema,
]);
export type UserMessageContent = z.infer<typeof UserMessageContentSchema>;

export const AssistantMessageContentSchema = z.union([
  TextPartSchema,
  ToolCallPartSchema,
  ReasoningPartSchema,
  RedactedReasoningPartSchema,
]);
export type AssistantMessageContent = z.infer<
  typeof AssistantMessageContentSchema
>;

// End Message Parts

// Messages
export const SystemMessageSchema_ = z.object({
  id: z.string().nullish(),
  role: z.literal("system"),
  content: z.string(),
});
export interface SystemMessage extends z.infer<typeof SystemMessageSchema_> {}
export const SystemMessageSchema: z.ZodType<SystemMessage> =
  SystemMessageSchema_;

export const UserMessageSchema_ = z.object({
  id: z.string().nullish(),
  role: z.literal("user"),
  content: z.union([z.string(), z.array(UserMessageContentSchema)]),
});
export interface UserMessage extends z.infer<typeof UserMessageSchema_> {}
export const UserMessageSchema: z.ZodType<UserMessage> = UserMessageSchema_;

export const AiProviderMetricsSchema = z.object({
  promptTokens: z.number().nullish(),
  completionTokens: z.number().nullish(),
  providerRequestTimeMs: z.number().nullish(),
});
export type AiProviderMetrics = z.infer<typeof AiProviderMetricsSchema>;

export const AssistantMessageSchema_ = z.object({
  id: z.string().nullish(),
  role: z.literal("assistant"),
  content: z.union([z.string(), z.array(AssistantMessageContentSchema)]),
  feedback: createEnum(["positive", "negative"], {
    enumName: "LlmAssistantMessageFeedback",
  }).nullish(),
  comment: z.string().nullish(),
  usage: AiProviderMetricsSchema.nullish(),
});
export interface AssistantMessage
  extends z.infer<typeof AssistantMessageSchema_> {}
export const AssistantMessageSchema: z.ZodType<AssistantMessage> =
  AssistantMessageSchema_;

export const ToolMessageSchema_ = z.object({
  id: z.string().nullish(),
  role: z.literal("tool"),
  content: z.array(ToolResultPartSchema),
});
export interface ToolMessage extends z.infer<typeof ToolMessageSchema_> {}
export const ToolMessageSchema: z.ZodType<ToolMessage> = ToolMessageSchema_;

export const MessageSchema_ = z.union([
  SystemMessageSchema,
  UserMessageSchema,
  AssistantMessageSchema,
  ToolMessageSchema,
]);
export type Message = z.infer<typeof MessageSchema_>;
export const MessageSchema: z.ZodType<Message> = MessageSchema_;

export const MessageRoleSchema = createEnum(
  ["system", "user", "assistant", "tool"],
  { enumName: "MessageRole" },
);
export type MessageRole = z.infer<typeof MessageRoleSchema>;
